import { useState, useMemo, useCallback } from 'react'
import { Select, Space, Badge, Button, Popover, App } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFieldLabels } from '../../hooks'
import { labelService } from '../../services/labelService'
import { errorService } from '../../services/errorService'
import type { UpdateLabelParams } from '../../types/labels.types'
import { InlineLabelEditor } from './InlineLabelEditor'

interface EditableFieldSelectProps {
  context: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  style?: React.CSSProperties
}

export function EditableFieldSelect({
  context,
  value,
  onChange,
  placeholder = 'Select field',
  style
}: EditableFieldSelectProps) {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const { data: labels, refetch } = useFieldLabels(context)
  const [popoverOpen, setPopoverOpen] = useState<string | null>(null)

  // Update label mutation
  const updateLabelMutation = useMutation({
    mutationFn: (params: UpdateLabelParams) => labelService.updateLabel(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['field-labels', context] })
      await refetch()
      message.success('Label updated successfully')
      setPopoverOpen(null)
    },
    onError: (error: any) => {
      errorService.showError(
        'Failed to Update Label',
        'An error occurred while updating the field label',
        [{ code: 'UPDATE_FAILED', message: error?.message || String(error) }]
      )
    }
  })

  // Filter visible fields
  const visibleFields = useMemo(() => {
    if (!labels) return []

    return Object.entries(labels.labels)
      .filter(([field]) => labels.visibility[field])
      .map(([field, label]) => ({
        value: field,
        label: label,
        isCustom: labels.isCustom[field],
        uid: labels.uids[field]
      }))
  }, [labels])

  // Handle label update
  const handleLabelUpdate = useCallback(
    async (fieldName: string, newLabel: string, visible: boolean) => {
      const field = visibleFields.find((f) => f.value === fieldName)
      await updateLabelMutation.mutateAsync({
        uid: field?.uid,
        context,
        fieldName,
        label: newLabel,
        visible
      })
    },
    [context, visibleFields, updateLabelMutation]
  )

  return (
    <Select
      value={value}
      onChange={onChange}
      showSearch
      placeholder={placeholder}
      style={style}
      filterOption={false}
      optionFilterProp="children"
      options={visibleFields.map((field) => ({
        value: field.value,
        label: (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <span>{field.label}</span>
            <Space size="small">
              {field.isCustom && <Badge color="blue" text="Custom" />}
              <Popover
                title="Edit Field Label"
                trigger="click"
                placement="right"
                open={popoverOpen === field.value}
                onOpenChange={(open) => {
                  if (open) {
                    setPopoverOpen(field.value)
                  } else {
                    setPopoverOpen(null)
                  }
                }}
                content={
                  <InlineLabelEditor
                    context={context}
                    fieldName={field.value}
                    currentLabel={field.label}
                    currentVisible={true}
                    onSave={handleLabelUpdate}
                    onCancel={() => {
                      setPopoverOpen(null)
                    }}
                  />
                }
              >
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  style={{ padding: 0, height: 'auto' }}
                />
              </Popover>
            </Space>
          </Space>
        )
      }))}
    />
  )
}

export default EditableFieldSelect
